import React, { useCallback, useEffect, useState } from 'react'
import { Button, useOverlay, InfoSheetPanel, Row, DangerButton } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import ZionForm, { useZionForm } from '@fs/zion-form'
import { trackEvent } from '@fs/zion-analytics'
import {
  getDanglingRecords,
  getImage,
  getNestedOrFloatingElementById,
  getPersonName,
  setDisplayHighlights,
  useSlsStateContext,
} from '@fs/zion-sls'
import { DeleteOverlay } from '../overlays'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'
import PanelHeader from '../editPanels/PanelHeader'
import RecordGroup from './RecordGroup'
import InfoOverlay from '../overlays/InfoOverlay'
import { useR2Highlights, useR2StateContext } from '../../../../providers/r2Provider/R2Context'
import { useSharedBrowserStateContext } from '../../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'

export default function RemovePerson({ hidden, handleDeletePerson }) {
  const [t] = useTranslation()
  const state = useSlsStateContext()
  const r2State = useR2StateContext()
  const { thumbnails } = useSharedBrowserStateContext()
  const { displayHighlightsApi, splitScreenDisplayHighlightsApi } = useR2Highlights()
  const { removePersonOverlay, closeAll } = useOverlayContext()
  const [personIdsToDelete, setPersonIdsToDelete] = useState([])
  const [, setIdsToDelete] = useState({})
  const deletePersonOverlay = useOverlay()
  const quickInfoOverlay = useOverlay()
  const currentImage = getImage(state?.images, state?.currentImageArk)
  const records = currentImage?.subElements || []
  const danglingRecords = getDanglingRecords(currentImage)
  const [personData, setPersonData] = useState({})

  const allRecords = [
    ...records.filter((record) => record?.subElements),
    ...danglingRecords.filter((danglingRecord) => danglingRecord?.persons),
  ]
  const floatingPersons =
    currentImage?.floatingElements?.filter((el) => el?.elementType === 'PERSON' && !el.superElements) || []

  const title = t('remove-person.delete-person.heading', 'Delete Names')
  const content = t(
    'remove-person.delete-person.content',
    'Deleting will remove the name and all of their content. This action can be undone in the Change History.'
  )

  // The back arrow on the panel was clicked
  const userClosedPanel = () => {
    closeAll()
  }

  const methods = useZionForm({
    mode: 'all',
    defaultValues: {
      recordPerson: undefined,
    },
  })

  // This function will add the highlight to the main and split screen images based on the selected persons
  const showHighlights = useCallback(
    (idValues) => {
      // Function to add the name highlights for the selected person ids
      const addNameHighlightsToImageArk = (personIds, imageArk, highlightsApi) => {
        const filteredHighlights = r2State.editHighlights
          .filter((highlight) => highlight.personIds.some((v) => personIds.indexOf(v) !== -1))
          .filter((f) => ['NAME_SURN', 'NAME_SUF', 'NAME_GN', 'NAME_PRE'].includes(f.fieldType))
          .filter((h) => h?.imageId === imageArk)

        setDisplayHighlights(filteredHighlights, highlightsApi)
      }

      addNameHighlightsToImageArk(idValues, state.currentImageArk, displayHighlightsApi)

      if (r2State.splitScreen) {
        // Fetching the image ark in the split screen
        const splitScreenArk = thumbnails?.[r2State.referenceFocusedIndex]?.ark

        addNameHighlightsToImageArk(idValues, splitScreenArk, splitScreenDisplayHighlightsApi)
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [r2State.editHighlights, r2State.referenceFocusedIndex, r2State.splitScreen, state.currentImageArk]
  )

  // This handles changes to the selected values and triggers the highlights for them.
  const handleSelectionChange = useCallback(
    (values) => {
      setIdsToDelete((ids) => {
        const selectedIdValues = Object.values({ ...ids, ...values }).flat()
        setPersonIdsToDelete(selectedIdValues)
        showHighlights(selectedIdValues)
        return { ...ids, ...values }
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [r2State.referenceFocusedIndex]
  )

  // Clearing the highlights when loading the component
  useEffect(() => {
    setDisplayHighlights([], displayHighlightsApi)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // eslint-disable-next-line react/jsx-no-useless-fragment
  if (hidden) return <></>

  return (
    <>
      <ZionForm
        onSubmit={() => {
          deletePersonOverlay.handleClick()
          trackEvent({
            event_name: 'click_action',
            link_name: `Delete: Open Overlay`,
          })
        }}
        methods={methods}
        id="removePersonForm"
      >
        <InfoSheetPanel
          {...{ ...removePersonOverlay, close: userClosedPanel }}
          title={t('navigation.back-button', 'Back')}
          headingColor="red"
          footer={
            <Row gutters="sm" alignX="end">
              <Button
                onClick={() => {
                  closeAll()
                }}
              >
                {t('edit-index.remove-person.cancel.button', 'Cancel')}
              </Button>
              <DangerButton
                disabled={personIdsToDelete.length < 1}
                type="submit"
                form="removePersonForm"
                linkName="Delete Persons"
              >
                {t('edit-index.remove-person.delete.button', 'Delete')}
              </DangerButton>
            </Row>
          }
        >
          <>
            <PanelHeader heading={title} caption={content} headingSize="H5" />
            {allRecords?.map((record, index) => (
              <RecordGroup
                record={record}
                key={record.id}
                showSeparator={index < (allRecords?.length || 0) - 1}
                onSelectionChange={handleSelectionChange}
                onInfoIconClick={quickInfoOverlay.open}
                setPersonData={setPersonData}
              />
            ))}
            {floatingPersons && floatingPersons.length > 0 && (
              <RecordGroup
                record={floatingPersons}
                key="floating-records"
                onSelectionChange={handleSelectionChange}
                onInfoIconClick={quickInfoOverlay.open}
                setPersonData={setPersonData}
                floatingRecords
              />
            )}
          </>
        </InfoSheetPanel>
      </ZionForm>

      <DeleteOverlay
        overlay={deletePersonOverlay}
        headingText={t('remove-person.delete-overlay.heading-text', `Delete All Selected`)}
        handleDelete={() => {
          handleDeletePerson(personIdsToDelete)
          closeAll()
        }}
        deleteMultipleView
        count={personIdsToDelete?.length ?? 0}
      >
        <div>
          <div>
            {personIdsToDelete.length === 1
              ? t(
                  'remove-person.delete-overlay.content-text_singular',
                  'Delete the following name from the document? This cannot be undone.'
                )
              : t(
                  'remove-person.delete-overlay.content-text_plural',
                  'Delete the following {count} names from the document? This cannot be undone.',
                  {
                    count: personIdsToDelete?.length ?? 0,
                  }
                )}
          </div>
          <br />
          <ul>
            {personIdsToDelete.map((personId) => {
              const person = getNestedOrFloatingElementById(state.images, personId)
              const personName = getPersonName({ person })
              return (
                person && (
                  <li data-testid={`delete-person-${personId}`} key={person.id}>
                    <strong>{personName}</strong>
                  </li>
                )
              )
            })}
          </ul>
        </div>
      </DeleteOverlay>

      <InfoOverlay overlayProps={quickInfoOverlay} person={personData} />
    </>
  )
}
